import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  FC,
  FocusEventHandler,
  UIEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GoTriangleDown } from "react-icons/go";
import { LuPlusCircle, LuX } from "react-icons/lu";
import Select, {
  components,
  ControlProps,
  GroupBase,
  IndicatorsContainerProps,
  InputActionMeta,
  MenuListProps,
  MenuPlacement,
  MenuProps,
  MultiValue,
  MultiValueProps,
  OptionProps,
  ValueContainerProps,
} from "react-select";

import { SelectOptionType } from "../../types/types";
import { AvatarPlaceholder } from "../avatar/AvatarPlaceholder";
import { LottieLoading } from "../graphics/LottieLoading";
import { FitChips } from "../newUI/chips";
import { insertAt, suggestionKeyAction } from "./suggestionKeys";

type CustomSelectSize = "sm" | "md" | "lg";

type IndicatorIconComponent = React.ComponentType<{
  size?: string | number;
  className?: string;
}>;

const SELECT_SIZE_CONFIG: Record<
  CustomSelectSize,
  { height: string; fontSize: string }
> = {
  sm: { height: "32px", fontSize: "14px" },
  md: { height: "36px", fontSize: "14px" },
  lg: { height: "40px", fontSize: "16px" },
};

export type CustomSelectStyle = {
  fullWidth?: boolean;
  small?: boolean;
  size?: CustomSelectSize;
  mail?: boolean;
  height?: string;
  isSubtask?: boolean;
  workTable?: boolean;
  width?: string;
  emailPage?: boolean;
  listHeight?: string;
  fontWeight?: number;
  controlStyle?: { backgroundColor?: string; borderColor?: string };
  /** Billing pages (V49 design): 38px high, 9px radius, 13px text. */
  billing?: boolean;
  /** Multi-select: as many chips as fit on one line, then "+N" (owner's rule for forms). */
  fitChips?: boolean;
  /**
   * Multi-select of clients: with more than one picked, shows
   * "N <countLabel> selected" instead of chips (e.g. "clients").
   */
  countLabel?: string;
};

interface Props {
  isMulti?: true | undefined;
  withAvatar?: boolean;
  autoFocus?: boolean;
  options: SelectOptionType[];
  placeholder?: string;
  onChange: (newValue: MultiValue<SelectOptionType> | SelectOptionType) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  value: SelectOptionType[] | SelectOptionType | null;
  inputValue?: string;
  onChangeInput?: (inputValue: string) => void;
  onMenuScrollToBottom?: () => void;
  clearValueCloseMenu?: boolean;
  removeSelectIcon?: boolean;
  isClearable?: boolean;
  onClear?: () => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  setIsOpenAddContact?: (value: boolean) => void;
  menuIsOpen?: boolean;
  withMenu?: boolean;
  isEditUser?: boolean;
  isError?: boolean;
  menuPlacement?: MenuPlacement;
  leadingIcon?: IndicatorIconComponent;
  indicatorIcon?: IndicatorIconComponent;
  renderOptionPreview?: (option: SelectOptionType) => React.ReactNode;
  selectStyle?: CustomSelectStyle;
  /**
   * Typing box with suggestions (QA batch 15, NIND-011 / NORG-008): Space types a
   * space (react-select would pick the highlighted option), and no option is
   * highlighted or picked with Enter until the person moves to it (arrow keys or
   * mouse). Clicking an option still picks it.
   */
  suggestionsOnly?: boolean;
}

type CustomSelectContextValue = {
  isLoading?: boolean;
  isLoadingMore?: boolean;
  withAvatar?: boolean;
  leadingIcon?: IndicatorIconComponent;
  isCompactAvatar?: boolean;
  setIsOpenAddContact?: (value: boolean) => void;
  hoveredOption?: SelectOptionType | null;
  setHoveredOption?: (option: SelectOptionType | null) => void;
  renderOptionPreview?: (option: SelectOptionType) => React.ReactNode;
  onClear?: () => void;
  countLabel?: string;
  compact?: boolean;
  fontSize?: string;
  /** suggestionsOnly and the person hasn't moved to an option yet: show none as highlighted. */
  hideFocus?: boolean;
  onOptionHover?: () => void;
};

const CustomSelectContext = React.createContext<CustomSelectContextValue>({});

type MenuListPropsType = MenuListProps<
  SelectOptionType,
  true,
  GroupBase<SelectOptionType>
>;

const MenuList = ({
  children,
  innerRef,
  innerProps,
  ...props
}: MenuListPropsType) => {
  const { setIsOpenAddContact, isLoading, isLoadingMore } =
    useContext(CustomSelectContext);

  const menuListRef = useRef<HTMLDivElement | null>(null);
  const scrollTopRef = useRef(0);

  const setMenuListRef = useCallback(
    (node: HTMLDivElement | null) => {
      menuListRef.current = node;

      if (typeof innerRef === "function") {
        innerRef(node);
      }
    },
    [innerRef],
  );

  useLayoutEffect(() => {
    const node = menuListRef.current;

    if (!node || scrollTopRef.current <= 0) {
      return;
    }

    requestAnimationFrame(() => {
      if (menuListRef.current) {
        menuListRef.current.scrollTop = scrollTopRef.current;
      }
    });
  }, [children]);

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    scrollTopRef.current = event.currentTarget.scrollTop;
    innerProps?.onScroll?.(event);
  };

  return (
    <components.MenuList
      {...props}
      innerRef={setMenuListRef}
      innerProps={{
        ...innerProps,
        onScroll: handleScroll,
      }}>
      {isLoading && !isLoadingMore ? (
        <LottieLoading />
      ) : (
        <>
          {setIsOpenAddContact && (
            <div
              className={
                "flex cursor-pointer items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-400 hover:text-white"
              }
              onClick={() => {
                props.selectProps.onMenuClose();
                setIsOpenAddContact(true);
              }}>
              <div
                className={
                  "flex h-4 w-4 items-center justify-center rounded-full border border-blue-600"
                }>
                <FontAwesomeIcon
                  icon={faPlus}
                  className={"text-[10px] text-blue-600"}
                />
              </div>

              <div className={"whitespace-nowrap font-bold"}>
                Create new contact
              </div>
            </div>
          )}

          {children}

          {isLoadingMore && (
            <div className={"flex justify-center py-2"}>
              <LottieLoading hideText />
            </div>
          )}
        </>
      )}
    </components.MenuList>
  );
};

type CustomMenuProps = MenuProps<
  SelectOptionType,
  true,
  GroupBase<SelectOptionType>
>;

const CustomMenu = ({ children, ...props }: CustomMenuProps) => {
  const { hoveredOption, renderOptionPreview } =
    useContext(CustomSelectContext);

  return (
    <components.Menu {...props}>
      <div className={"relative"}>
        {children}

        {hoveredOption && renderOptionPreview && (
          <div className={"absolute left-[calc(100%+8px)] top-0 z-[1]"}>
            {renderOptionPreview(hoveredOption)}
          </div>
        )}
      </div>
    </components.Menu>
  );
};

type InputOptionProps = OptionProps<
  SelectOptionType,
  true,
  GroupBase<SelectOptionType>
>;

const InputOption = ({
  getStyles,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  data,
  isMulti,
  mail,
  fontWeight,
  ...rest
}: InputOptionProps & { mail?: boolean; fontWeight?: number }) => {
  const [isActive, setIsActive] = useState(false);
  const { setHoveredOption, hideFocus, onOptionHover } =
    useContext(CustomSelectContext);

  let bg = "transparent";
  if (isFocused && !hideFocus) bg = "#eee";
  if (isActive) bg = "#B2D4FF";

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex",
    fontWeight: fontWeight || 500,
    fontSize: mail ? "12px" : "14px",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    overflow: "hidden",
    cursor: "pointer",
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif",
  };

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isMulti={isMulti}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      data={data}
      innerProps={{
        ...innerProps,
        onMouseEnter: (event) => {
          innerProps.onMouseEnter?.(event);
          setHoveredOption?.(data);
          onOptionHover?.();
        },
        onMouseDown: () => setIsActive(true),
        onMouseUp: () => setIsActive(false),
        onMouseLeave: () => setIsActive(false),
        style,
      }}>
      {isMulti && (
        <input
          type={"checkbox"}
          checked={isSelected}
          onChange={() => null}
          className={
            "checkbox mr-2 scale-75 rounded-[5px] border-gray-300 [--chkbg:--b1] [--chkfg:--a] checked:border-gray-300 checked:bg-base-100"
          }
        />
      )}

      {data?.color && (
        <div
          style={{ backgroundColor: data.color }}
          className={"mr-2 h-[10px] w-[10px] rounded-full"}
        />
      )}

      {data?.avatar && (
        <div className={"mr-2 font-normal"}>
          <AvatarPlaceholder
            size={"2xs"}
            label={data.avatar}
            avatarCustomColor={data.avatarCustomColor || data.avatarColor}
            type={data.avatarColor || "custom"}
          />
        </div>
      )}

      {data.value === "create" ? (
        <div
          className={
            "flex items-center gap-2 whitespace-nowrap font-bold text-blue-600"
          }>
          <LuPlusCircle size={16} />
          {children}
        </div>
      ) : (
        <div className={"grid grid-rows-[max-content_max-content]"}>
          <div className={data?.isBold ? "font-bold" : "!font-[400]"}>
            {children}
          </div>

          {data?.caption && (
            <div className={"truncate text-xs text-gray-500"}>
              {data.caption}
            </div>
          )}
        </div>
      )}
    </components.Option>
  );
};

type CustomMultiValueProps = MultiValueProps<
  SelectOptionType,
  true,
  GroupBase<SelectOptionType>
>;

const CustomMultiValue = (props: CustomMultiValueProps) => {
  const { children, data, getValue } = props;

  const totalValues = getValue();
  const indexItem = totalValues.findIndex((item) => item.value === data.value);

  if (indexItem !== 0) {
    return indexItem > 1 ? null : <span>...</span>;
  }

  return <components.MultiValue {...props}>{children}</components.MultiValue>;
};

type FitChipsValueContainerProps = ValueContainerProps<
  SelectOptionType,
  true,
  GroupBase<SelectOptionType>
>;

/** Shows the selected values with FitChips instead of react-select's chips. */
const FitChipsValueContainer = (props: FitChipsValueContainerProps) => {
  const { children, getValue, hasValue, selectProps } = props;
  const { countLabel, compact, fontSize } = useContext(CustomSelectContext);
  const values = getValue();
  const multiValueComponent = selectProps.components?.MultiValue;
  const rest = React.Children.toArray(children).filter(
    (child) =>
      !(React.isValidElement(child) && child.type === multiValueComponent),
  );
  const showCount = !!countLabel && values.length > 1;

  return (
    <components.ValueContainer {...props}>
      {hasValue && showCount && (
        <span
          className={
            "min-w-0 flex-1 truncate font-medium leading-[18px] text-[#1F2433]"
          }
          style={{ fontSize: fontSize ?? "14px" }}
          title={values.map((option) => option.label).join(", ")}>
          {values.length} {countLabel} selected
        </span>
      )}
      {hasValue && !showCount && (
        <FitChips
          items={values.map((option) => ({
            key: String(option.value),
            label: String(option.label),
          }))}
          compact={compact}
          reserve={selectProps.inputValue ? 60 : 8}
          onRemove={(key) => {
            const removedValue = values.find(
              (option) => String(option.value) === key,
            );
            if (!removedValue) return;
            selectProps.onChange(
              values.filter((option) => String(option.value) !== key),
              { action: "remove-value", removedValue },
            );
          }}
        />
      )}
      {rest}
    </components.ValueContainer>
  );
};

type CustomControlProps = ControlProps<
  SelectOptionType,
  true,
  GroupBase<SelectOptionType>
>;

const CustomControl = (props: CustomControlProps) => {
  const { children, getValue } = props;
  const {
    withAvatar,
    leadingIcon: LeadingIcon,
    isCompactAvatar,
  } = useContext(CustomSelectContext);

  const value = getValue()[0] || null;

  return (
    <components.Control {...props}>
      {LeadingIcon && (
        <div className={"flex shrink-0 items-center text-[#6B7280]"}>
          <LeadingIcon size={"18px"} />
        </div>
      )}

      {value?.color && (
        <div
          style={{ backgroundColor: value.color }}
          className={`ml-2 h-[10px] w-[10px] shrink-0 rounded-full${
            LeadingIcon ? " ml-2" : ""
          }`}
        />
      )}

      {value && withAvatar && (
        <div className={`mr-2 flex items-center${LeadingIcon ? " ml-2" : ""}`}>
          <AvatarPlaceholder
            size={isCompactAvatar ? "2xs" : "super-small"}
            label={value.avatar || ""}
            type={value.avatarColor || value.type || "custom"}
            avatarCustomColor={value.avatarCustomColor || value.avatarColor}
          />
        </div>
      )}

      {children}
    </components.Control>
  );
};

type CustomIndicatorsContainerProps = IndicatorsContainerProps<
  SelectOptionType,
  true,
  GroupBase<SelectOptionType>
> & {
  icon: IndicatorIconComponent;
  isClearable?: boolean;
};

const CustomIndicatorsContainer = ({
  icon: Icon,
  isClearable,
  ...props
}: CustomIndicatorsContainerProps) => {
  const { onClear } = useContext(CustomSelectContext);

  if (isClearable && props.hasValue) {
    return (
      <components.IndicatorsContainer {...props}>
        <button
          type={"button"}
          aria-label={"Clear selected value"}
          className={
            "flex h-6 w-6 items-center justify-center rounded-md text-[#9CA3AF] hover:bg-gray-100 hover:text-[#4B5563]"
          }
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onClear?.();
          }}>
          <LuX size={"18px"} />
        </button>
      </components.IndicatorsContainer>
    );
  }

  return (
    <components.IndicatorsContainer {...props}>
      <Icon size={"14px"} />
    </components.IndicatorsContainer>
  );
};

const CustomSelect: FC<Props> = ({
  isMulti,
  options,
  onChange,
  onBlur,
  placeholder,
  value,
  inputValue,
  onChangeInput,
  onMenuScrollToBottom,
  clearValueCloseMenu = true,
  removeSelectIcon,
  isClearable,
  onClear,
  isLoading,
  isLoadingMore,
  withAvatar,
  autoFocus,
  setIsOpenAddContact,
  menuIsOpen,
  withMenu,
  isError,
  menuPlacement = "auto",
  leadingIcon,
  indicatorIcon = GoTriangleDown,
  renderOptionPreview,
  selectStyle,
  suggestionsOnly,
}) => {
  const {
    fullWidth,
    small,
    size,
    mail,
    isSubtask,
    workTable,
    width,
    emailPage,
    listHeight,
    fontWeight,
    controlStyle,
    billing,
    fitChips,
    countLabel,
  } = selectStyle || {};
  const useFitValues = !!isMulti && (!!fitChips || !!countLabel);
  const selectSize = small ? "sm" : size;
  const selectSizeConfig = selectSize ? SELECT_SIZE_CONFIG[selectSize] : null;
  const selectFontSize = billing ? "13px" : selectSizeConfig?.fontSize;
  const selectHeight = billing ? "38px" : selectSizeConfig?.height;
  const focusColor = billing ? "#6B4EFF" : "#3e4bff";
  const isCompactAvatar = selectSize === "sm" || selectSize === "md";

  const [inputValueState, setInputValueState] = useState(inputValue ?? "");
  const [hoveredOption, setHoveredOption] = useState<SelectOptionType | null>(
    null,
  );
  // suggestionsOnly: has the person moved to an option (arrow keys / mouse)?
  const [movedToOption, setMovedToOption] = useState(false);
  const markMoved = useCallback(() => setMovedToOption(true), []);

  useEffect(() => {
    setInputValueState(inputValue ?? "");
  }, [inputValue]);

  const clearSearch = useCallback(() => {
    setInputValueState("");
    onChangeInput?.("");
  }, [onChangeInput]);

  const handleInputChange = (nextValue: string, meta: InputActionMeta) => {
    if (meta.action === "input-change") {
      setInputValueState(nextValue);
      onChangeInput?.(nextValue);
      setMovedToOption(false);
    }

    return nextValue;
  };

  const handleSuggestionKeys = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!suggestionsOnly) return;
    const action = suggestionKeyAction(event.key, movedToOption);
    if (action === "default") return;
    event.preventDefault();
    if (action === "show-first") {
      setMovedToOption(true);
      return;
    }
    if (action !== "type-space") return; // "ignore": Enter before moving
    const input = event.target as HTMLInputElement;
    const current = inputValueState;
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? start;
    const next = insertAt(current, start, end, " ");
    setInputValueState(next);
    onChangeInput?.(next);
    setMovedToOption(false);
    requestAnimationFrame(() => {
      try {
        input.setSelectionRange(start + 1, start + 1);
      } catch {
        // the input may be gone
      }
    });
  };

  const handleChange = (
    newValue: MultiValue<SelectOptionType> | SelectOptionType,
  ) => {
    onChange(newValue);
    clearSearch();
  };

  const customSelectContextValue = useMemo(
    () => ({
      isLoading,
      isLoadingMore,
      withAvatar,
      leadingIcon,
      isCompactAvatar,
      setIsOpenAddContact,
      hoveredOption,
      setHoveredOption,
      renderOptionPreview,
      onClear: () => {
        onClear?.();
        clearSearch();
      },
      countLabel,
      compact: workTable,
      fontSize: selectFontSize,
      hideFocus: !!suggestionsOnly && !movedToOption,
      onOptionHover: suggestionsOnly ? markMoved : undefined,
    }),
    [
      suggestionsOnly,
      movedToOption,
      markMoved,
      countLabel,
      workTable,
      selectFontSize,
      isLoading,
      isLoadingMore,
      withAvatar,
      leadingIcon,
      isCompactAvatar,
      setIsOpenAddContact,
      hoveredOption,
      renderOptionPreview,
      onClear,
      clearSearch,
    ],
  );

  return (
    <CustomSelectContext.Provider value={customSelectContextValue}>
      <Select
        options={options}
        value={value}
        onChange={handleChange}
        onKeyDown={suggestionsOnly ? handleSuggestionKeys : undefined}
        onMenuClose={() => {
          setHoveredOption(null);
          setMovedToOption(false);

          if (!clearValueCloseMenu) {
            setInputValueState(inputValue ?? "");
          }
        }}
        menuIsOpen={menuIsOpen}
        onBlur={onBlur}
        placeholder={placeholder}
        inputValue={inputValueState}
        onInputChange={handleInputChange}
        onMenuScrollToBottom={onMenuScrollToBottom}
        isLoading={isLoading}
        isMulti={isMulti}
        autoFocus={autoFocus}
        closeMenuOnSelect={!isMulti}
        hideSelectedOptions={false}
        filterOption={null}
        tabSelectsValue={false}
        menuPortalTarget={document.body}
        menuPlacement={menuPlacement}
        components={{
          Option: (props) => (
            <InputOption mail={mail} fontWeight={fontWeight} {...props}>
              {props.children}
            </InputOption>
          ),
          MultiValue: CustomMultiValue,
          ...(useFitValues ? { ValueContainer: FitChipsValueContainer } : {}),
          Control: CustomControl,
          Menu: CustomMenu,
          MenuList,
          IndicatorsContainer: (props) => (
            <CustomIndicatorsContainer
              icon={indicatorIcon}
              isClearable={isClearable}
              {...props}>
              {props.children}
            </CustomIndicatorsContainer>
          ),
          IndicatorSeparator: isSubtask || billing ? () => null : undefined,
        }}
        styles={{
          indicatorsContainer: (base, state) =>
            removeSelectIcon && !(isClearable && state.hasValue)
              ? { display: "none" }
              : {
                  ...base,
                  maxHeight: isSubtask
                    ? "38px"
                    : (small && mail) || workTable
                    ? "40px"
                    : selectHeight
                    ? selectHeight
                    : undefined,
                },
          input: (base, state) => ({
            ...base,
            // Leave the free space to the chips; the box grows while typing.
            ...(useFitValues && state.hasValue ? { flex: "0 1 auto" } : {}),
            fontSize: selectFontSize ?? (workTable ? "14px" : "16px"),
            border: "none",
            fontWeight: fontWeight || 600,
            cursor: "pointer",
            height: workTable ? "16px" : "",
            padding: workTable ? "0px" : "",
            margin: workTable ? "0px" : "",
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
            fontWeight: fontWeight || 500,
            width: width || "250px",
            display:
              withMenu || inputValueState || emailPage ? "block" : "none",
          }),
          placeholder: (base) => ({
            ...base,
            fontSize: selectFontSize ?? (workTable ? "14px" : "16px"),
            fontWeight: billing ? 400 : fontWeight || 500,
            color: billing ? "#9AA0B0" : "rgba(51,51,51,0.6)",
            height: workTable ? "16px" : "",
            marginTop: workTable ? "-4px" : "0px",
          }),
          singleValue: (base) => ({
            ...base,
            fontSize: mail ? "12px" : selectFontSize ?? "14px",
            fontWeight: fontWeight || 400,
            cursor: "pointer",
            paddingBottom: workTable ? "0px" : "",
          }),
          multiValue: (base) => ({
            ...base,
            maxWidth: "150px",
            textOverflow: "ellipsis",
            borderRadius: "8px",
            backgroundColor: "#EDE9FE",
            color: "#6938EF",
            fontSize: "14px",
            height: workTable ? "24px" : "",
          }),
          multiValueLabel: (base) => ({
            ...base,
            color: "#6938EF",
            fontWeight: 600,
            paddingLeft: "8px",
          }),
          multiValueRemove: (base) => ({
            ...base,
            borderRadius: "0 8px 8px 0",
            color: "#6938EF",
            height: workTable ? "30px" : "",
          }),
          valueContainer: (base) => ({
            ...base,
            flexWrap: "nowrap",
            maxHeight: workTable
              ? // Room for the chips, which are 18px high (Roadmap 1859).
                useFitValues
                ? "24px"
                : "16px"
              : isSubtask
              ? "38px"
              : selectHeight
              ? selectHeight
              : undefined,
            paddingLeft: withAvatar || workTable || leadingIcon ? "0px" : "8px",
            cursor: "pointer",
            padding: workTable ? "0px" : "",
            // The search input is taller than this box; focusing it after a
            // pick scrolled the box and cut the chips' tops (Roadmap 1859).
            // "clip" can't be scrolled.
            ...(workTable && useFitValues ? { overflow: "clip" } : {}),
          }),
          menuList: (base) => ({
            ...base,
            maxHeight: listHeight || "300px",
            fontWeight: fontWeight || 400,
          }),
          menu: (base) => ({
            ...base,
            overflow: "visible",
          }),
          control: (base, state) => ({
            ...base,
            minHeight: isSubtask
              ? "38px"
              : (small && mail) || workTable
              ? "40px"
              : selectHeight
              ? selectHeight
              : "48px",
            maxHeight:
              (small && mail) || workTable
                ? "40px"
                : selectHeight
                ? selectHeight
                : undefined,
            maxWidth: fullWidth ? "100%" : "300px",
            cursor: "pointer",
            boxShadow:
              isError && state?.isFocused
                ? "0 0 0 1px #DC2626"
                : isError
                ? "none"
                : state?.isFocused && isSubtask
                ? "0 0 0 1px #EAEDF3A3"
                : state?.isFocused
                ? `0 0 0 1px ${focusColor}`
                : "none",
            borderColor: isError
              ? "#DC2626"
              : state?.isFocused && isSubtask
              ? "#EAEDF3A3"
              : state?.isFocused
              ? focusColor
              : controlStyle?.borderColor ??
                (billing ? "#DFE2E8" : "#1f293733"),
            borderRadius: workTable ? "6px" : billing ? "9px" : "0.5rem",
            backgroundColor: isSubtask
              ? "#F8F9FB"
              : controlStyle?.backgroundColor ?? "",
            paddingLeft: workTable ? "16px" : withAvatar ? "8px" : "0px",
            paddingRight: workTable ? "16px" : "4px",
            "&:hover": {
              borderColor: isError
                ? "#DC2626"
                : isSubtask
                ? "none"
                : focusColor,
            },
          }),
        }}
      />
    </CustomSelectContext.Provider>
  );
};

export default CustomSelect;
